import { formatResult, isAsyncIterable, isObservable, formatError, fillLeafs } from "@graphiql/toolkit";
import { print } from "graphql";
import { getFragmentDependenciesForAST } from "graphql-language-service";
import setValue from "set-value";
import getValue from "get-value";
import { getRunBlockReason, resolveActiveOperation } from "../utility/run-block.js";
import { STORAGE_KEY } from "../constants.js";
import { tryParseJSONC } from "../utility/jsonc.js";
import { Range } from "monaco-editor/esm/vs/editor/common/core/range.js";
const RESPONSE_VIEWS = ["json", "tree", "table"];
const byteLength = (value) => typeof TextEncoder === "undefined" ? value.length : new TextEncoder().encode(value).length;
function buildFetcherFallbackResponse(body, startMs, formattedBody) {
  const errors = body == null ? void 0 : body.errors;
  return {
    ok: !Array.isArray(errors) || errors.length === 0,
    body,
    timing: {
      totalMs: Date.now() - startMs
    },
    size: {
      response: byteLength(formattedBody)
    }
  };
}
function buildErrorResponse(errorText, startMs) {
  return {
    ok: false,
    body: {
      errors: [{
        message: errorText
      }]
    },
    timing: {
      totalMs: Date.now() - startMs
    },
    size: {
      response: byteLength(errorText)
    }
  };
}
function isResponseView(value) {
  return typeof value === "string" && RESPONSE_VIEWS.includes(value);
}
const createExecutionSlice = (initial) => (set, get) => {
  var _a;
  function getAutoCompleteLeafs() {
    const {
      queryEditor,
      schema,
      getDefaultFieldNames
    } = get();
    if (!queryEditor) {
      return;
    }
    const query = queryEditor.getValue();
    const {
      insertions,
      result = ""
    } = fillLeafs(schema, query, getDefaultFieldNames);
    if (!insertions.length) {
      return result;
    }
    const model = queryEditor.getModel();
    const selection = queryEditor.getSelection();
    const cursorIndex = model.getOffsetAt(selection.getPosition());
    model.setValue(result);
    let added = 0;
    const decorations = insertions.map(({
      index,
      string
    }) => {
      const start = model.getPositionAt(index + added);
      const end = model.getPositionAt(index + (added += string.length));
      return {
        range: new Range(start.lineNumber, start.column, end.lineNumber, end.column),
        options: {
          className: "auto-inserted-leaf",
          hoverMessage: {
            value: "Automatically added leaf fields"
          },
          isWholeLine: false
        }
      };
    });
    const decorationCollection = queryEditor.createDecorationsCollection([]);
    decorationCollection.set(decorations);
    setTimeout(() => {
      decorationCollection.clear();
    }, 7e3);
    let newCursorIndex = cursorIndex;
    for (const {
      index,
      string
    } of insertions) {
      if (index < cursorIndex) {
        newCursorIndex += string.length;
      }
    }
    const newCursorPosition = model.getPositionAt(newCursorIndex);
    queryEditor.setPosition(newCursorPosition);
    return result;
  }
  return {
    ...initial,
    isFetching: false,
    lastResponse: null,
    responseView: "json",
    transportUpgradeBannerDismissed: false,
    subscription: null,
    abortController: null,
    queryId: 0,
    transportMethod: ((_a = initial.transport) == null ? void 0 : _a.method) ?? null,
    actions: {
      setResponseView(view) {
        const {
          storage
        } = get();
        storage.set(STORAGE_KEY.responseView, view);
        set({
          responseView: view
        });
      },
      dismissTransportUpgradeBanner() {
        const {
          storage
        } = get();
        storage.set(STORAGE_KEY.transportUpgradeBannerDismissed, "true");
        set({
          transportUpgradeBannerDismissed: true
        });
      },
      setTransportMethod(method) {
        const {
          transport
        } = get();
        if (!(transport == null ? void 0 : transport.setMethod)) {
          return;
        }
        transport.setMethod(method);
        set({
          transportMethod: method
        });
      },
      stop() {
        set(({
          subscription,
          abortController,
          queryId
        }) => {
          subscription == null ? void 0 : subscription.unsubscribe();
          abortController == null ? void 0 : abortController.abort();
          return {
            isFetching: false,
            subscription: null,
            abortController: null,
            // Invalidate any response still on its way from a transport
            // that ignores the abort signal — `handleResponse` drops
            // results whose queryId is stale.
            queryId: queryId + 1
          };
        });
      },
      async run() {
        var _a2;
        const {
          externalFragments,
          headerEditor,
          queryEditor,
          responseEditor,
          variableEditor,
          actions,
          operationName,
          operations,
          documentAST,
          subscription,
          overrideOperationName,
          queryId,
          fetcher,
          transport,
          transportMethod
        } = get();
        if (!queryEditor || !responseEditor) {
          return;
        }
        if (subscription) {
          actions.stop();
          return;
        }
        const blockReason = getRunBlockReason(transportMethod, resolveActiveOperation(operations, operationName));
        if (blockReason) {
          return;
        }
        function setResponse(value) {
          responseEditor == null ? void 0 : responseEditor.setValue(value);
          actions.updateActiveTabValues({
            response: value
          });
        }
        function setError(error, editor) {
          if (!editor) {
            return;
          }
          const name = editor === variableEditor ? "Variables" : "Request headers";
          setResponse(formatError({
            message: `${name} ${error.message}`
          }));
        }
        const newQueryId = queryId + 1;
        set({
          queryId: newQueryId
        });
        let query = getAutoCompleteLeafs() || queryEditor.getValue();
        let variables;
        try {
          variables = tryParseJSONC(variableEditor == null ? void 0 : variableEditor.getValue());
        } catch (error) {
          setError(error, variableEditor);
          return;
        }
        let headers;
        try {
          headers = tryParseJSONC(headerEditor == null ? void 0 : headerEditor.getValue());
        } catch (error) {
          setError(error, headerEditor);
          return;
        }
        const fragmentDependencies = documentAST ? getFragmentDependenciesForAST(documentAST, externalFragments) : [];
        if (fragmentDependencies.length) {
          query += "\n" + fragmentDependencies.map((node) => print(node)).join("\n");
        }
        setResponse("");
        set({
          isFetching: true
        });
        const fetchStartMs = Date.now();
        let controller;
        try {
          const fullResponse = {};
          const handleResponse = (result, envelope) => {
            if (newQueryId !== get().queryId) {
              return;
            }
            let maybeMultipart = Array.isArray(result) ? result : false;
            if (!maybeMultipart && typeof result === "object" && "hasNext" in result) {
              maybeMultipart = [result];
            }
            let formattedBody;
            let mergedBody;
            if (maybeMultipart) {
              for (const part of maybeMultipart) {
                mergeIncrementalResult(fullResponse, part);
              }
              mergedBody = fullResponse;
              formattedBody = formatResult(fullResponse);
            } else {
              mergedBody = result;
              formattedBody = formatResult(result);
            }
            set({
              isFetching: false,
              lastResponse: envelope ?? buildFetcherFallbackResponse(mergedBody, fetchStartMs, formattedBody)
            });
            setResponse(formattedBody);
          };
          const opName = overrideOperationName ?? operationName;
          if (transport) {
            (_a2 = get().abortController) == null ? void 0 : _a2.abort();
            controller = new AbortController();
            set({
              abortController: controller
            });
            const result = transport.send({
              query,
              variables,
              operationName: opName,
              headers,
              signal: controller.signal
            });
            if (isAsyncIterable(result)) {
              const it = result[Symbol.asyncIterator]();
              const newSubscription = {
                unsubscribe: () => {
                  var _a3;
                  return (_a3 = it.return) == null ? void 0 : _a3.call(it);
                }
              };
              set({
                subscription: newSubscription
              });
              for await (const tr of {
                [Symbol.asyncIterator]: () => it
              }) {
                handleResponse(tr.body, tr);
              }
              if (newQueryId === get().queryId) {
                set({
                  isFetching: false,
                  subscription: null,
                  abortController: null
                });
              }
            } else {
              const tr = await result;
              handleResponse(tr.body, tr);
              if (newQueryId === get().queryId) {
                set({
                  abortController: null
                });
              }
            }
          } else if (fetcher) {
            const fetch = fetcher({
              query,
              variables,
              operationName: opName
            }, {
              headers,
              documentAST
            });
            const value = await fetch;
            if (isObservable(value)) {
              const newSubscription = value.subscribe({
                next(result) {
                  handleResponse(result);
                },
                error(error) {
                  const errorText = formatError(error);
                  set({
                    isFetching: false,
                    lastResponse: buildErrorResponse(errorText, fetchStartMs),
                    subscription: null
                  });
                  setResponse(errorText);
                },
                complete() {
                  set({
                    isFetching: false,
                    subscription: null
                  });
                }
              });
              set({
                subscription: newSubscription
              });
            } else if (isAsyncIterable(value)) {
              const it = value[Symbol.asyncIterator]();
              const newSubscription = {
                unsubscribe: () => {
                  var _a3;
                  return (_a3 = it.return) == null ? void 0 : _a3.call(it);
                }
              };
              set({
                subscription: newSubscription
              });
              for await (const result of {
                [Symbol.asyncIterator]: () => it
              }) {
                handleResponse(result);
              }
              if (newQueryId === get().queryId) {
                set({
                  isFetching: false,
                  subscription: null
                });
              }
            } else {
              handleResponse(value);
            }
          } else {
            throw new Error("No fetcher or transport configured on `GraphiQLProvider`.");
          }
        } catch (error) {
          if ((controller == null ? void 0 : controller.signal.aborted) || newQueryId !== get().queryId) {
            return;
          }
          const errorText = formatError(error);
          set({
            isFetching: false,
            lastResponse: buildErrorResponse(errorText, fetchStartMs),
            subscription: null,
            abortController: null
          });
          setResponse(errorText);
        }
      }
    }
  };
};
const pathsMap = /* @__PURE__ */ new WeakMap();
function mergeIncrementalResult(executionResult, incrementalResult) {
  var _a, _b, _c;
  let path = ["data", ...incrementalResult.path ?? []];
  for (const result of [executionResult, incrementalResult]) {
    if (result.pending) {
      let paths = pathsMap.get(executionResult);
      if (paths === void 0) {
        paths = /* @__PURE__ */ new Map();
        pathsMap.set(executionResult, paths);
      }
      for (const {
        id: id2,
        path: pendingPath
      } of result.pending) {
        paths.set(id2, ["data", ...pendingPath]);
      }
    }
  }
  const {
    items,
    data,
    id
  } = incrementalResult;
  if (items) {
    if (id) {
      path = (_a = pathsMap.get(executionResult)) == null ? void 0 : _a.get(id);
      if (path === void 0) {
        throw new Error("Invalid incremental delivery format.");
      }
      const list = getValue(executionResult, path.join("."));
      list.push(...items);
    } else {
      path = ["data", ...incrementalResult.path ?? []];
      for (const item of items) {
        setValue(executionResult, path.join("."), item);
        path[path.length - 1]++;
      }
    }
  }
  if (data) {
    if (id) {
      path = (_b = pathsMap.get(executionResult)) == null ? void 0 : _b.get(id);
      if (path === void 0) {
        throw new Error("Invalid incremental delivery format.");
      }
      const {
        subPath
      } = incrementalResult;
      if (subPath !== void 0) {
        path = [...path, ...subPath];
      }
    }
    setValue(executionResult, path.join("."), data, {
      merge: true
    });
  }
  if (incrementalResult.errors) {
    executionResult.errors || (executionResult.errors = []);
    executionResult.errors.push(...incrementalResult.errors);
  }
  if (incrementalResult.extensions) {
    setValue(executionResult, "extensions", incrementalResult.extensions, {
      merge: true
    });
  }
  if (incrementalResult.incremental) {
    for (const incrementalSubResult of incrementalResult.incremental) {
      mergeIncrementalResult(executionResult, incrementalSubResult);
    }
  }
  if (incrementalResult.completed) {
    for (const {
      id: completedId,
      errors
    } of incrementalResult.completed) {
      (_c = pathsMap.get(executionResult)) == null ? void 0 : _c.delete(completedId);
      if (errors) {
        executionResult.errors || (executionResult.errors = []);
        executionResult.errors.push(...errors);
      }
    }
  }
}
export {
  createExecutionSlice,
  isResponseView
};
//# sourceMappingURL=execution.js.map
